import styled from "styled-components";
import { MediaQuery } from "../../hooks/useDeviceType";
import {
  GAINSBORO,
  GHOST_WHITE,
  LIGHT_EERIE_BLACK,
  PRIMARY_FONT,
  SECONDARY_FONT,
  SLIM_WEIGHT,
  SMALL_SECONDARY_SIZE,
  SMALL_TITLE_SIZE,
} from "../../utils/variables";

export const Container = styled.section`
  max-width: 110rem;
  margin: 0 auto;
  padding: 10rem 12rem 10rem;

  @media ${MediaQuery.isTablet} {
    padding: 8rem 6rem;
  }

  @media ${MediaQuery.isMobile} {
    padding: 6rem 2rem 5rem;
  }
`;

export const HeadingContainer = styled.div`
  display: flex;
  align-items: center;
  font-family: ${SECONDARY_FONT};
  margin-bottom: 4rem;

  &::after {
    content: "";
    width: 100%;
    height: 1px;
    display: block;
    background-color: ${GAINSBORO};
    opacity: 0.4;
    margin-left: 2rem;
  }
`;

export const Heading = styled.h2`
  margin-left: 1rem;
  font-family: ${PRIMARY_FONT};
  font-size: ${SMALL_TITLE_SIZE};
  white-space: nowrap;
`;

export const ProjectsContainer = styled.section`
  display: flex;
  flex-direction: column;
  gap: 12rem;

  @media ${MediaQuery.isTablet} {
    gap: 8rem;
  }

  @media ${MediaQuery.isMobile} {
    gap: 6rem;
  }
`;

export const ProjectDescription = styled.div`
  width: 100%;
  text-align: left;
  background-color: ${LIGHT_EERIE_BLACK};
  padding: 2.4rem;
  border: 1px solid rgba(238, 240, 242, 0.08);
  border-radius: 0.8rem;
  box-shadow: 0 1.8rem 4rem rgba(0, 0, 0, 0.24);
`;

export const ProjectContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: ${(props) => (props.$reversed ? "flex-start" : "flex-end")};
  text-align: ${(props) => (props.$reversed ? "left" : "right")};
  width: 46%;
  margin-left: ${(props) => (props.$reversed ? "0" : "-6rem")};
  margin-right: ${(props) => (props.$reversed ? "-6rem" : "0")};
  position: relative;
  z-index: 2;

  @media ${MediaQuery.isTablet} {
    width: calc(100% - 4rem);
    margin: -5rem auto 0;
    padding: 2.4rem;
    align-items: flex-start;
    text-align: left;
    border-radius: 0.8rem;
    background-color: ${LIGHT_EERIE_BLACK};
    box-shadow: 0 1.8rem 4rem rgba(0, 0, 0, 0.24);

    ${ProjectDescription} {
      padding: 0;
      border: none;
      box-shadow: none;
    }
  }

  @media ${MediaQuery.isMobile} {
    width: calc(100% - 2rem);
    margin: -2rem auto 0;
    padding: 2.2rem 2rem 2rem;
    align-items: flex-start;
    text-align: left;
    border: 1px solid rgba(238, 240, 242, 0.08);
    border-radius: 1rem;
    background-color: ${LIGHT_EERIE_BLACK};
    box-shadow: 0 1.8rem 4rem rgba(0, 0, 0, 0.24);

    ${ProjectDescription} {
      padding: 0;
      border: none;
      background: transparent;
      box-shadow: none;
    }
  }
`;
export const Text = styled.p`
  color: ${GAINSBORO};
  font-weight: ${SLIM_WEIGHT};
  font-size: ${SMALL_SECONDARY_SIZE};
  line-height: 2.3rem;
`;

export const ProjectImage = styled.img`
  display: block;
  width: 100%;
  height: auto;
  aspect-ratio: 16 / 10;
  object-fit: cover;
  object-position: top center;
  border-radius: 0.8rem;
  opacity: 0.82;
  transition: opacity 0.25s ease, transform 0.35s ease;
`;

export const ProjectImageLink = styled.a`
  display: block;
  width: 66%;
  overflow: hidden;
  border-radius: 0.8rem;
  background-color: ${LIGHT_EERIE_BLACK};
  box-shadow: 0 2rem 5rem rgba(0, 0, 0, 0.28);

  &:hover ${ProjectImage},
  &:focus-visible ${ProjectImage} {
    opacity: 1;
    transform: scale(1.015);
  }

  &:focus-visible {
    outline: 2px solid ${GHOST_WHITE};
    outline-offset: 0.4rem;
  }

  @media ${MediaQuery.isTablet} {
    width: 100%;
  }

  @media ${MediaQuery.isMobile} {
    width: 100%;
    border-radius: 1rem;

    ${ProjectImage} {
      opacity: 1;
    }
  }
`;

export const ProjectName = styled.h3`
  font-family: ${PRIMARY_FONT};
  font-size: 3rem;
  margin: 0.8rem 0 2rem;

  @media ${MediaQuery.isMobile} {
    font-size: ${SMALL_TITLE_SIZE};
    margin-bottom: 1.4rem;
  }
`;

export const ProjectTitleLink = styled.a`
  color: ${GHOST_WHITE};
  text-decoration: none;

  &:hover,
  &:focus-visible {
    color: ${GAINSBORO};
  }
`;

export const ProjectLabel = styled.p`
  color: ${GAINSBORO};
  font-family: ${SECONDARY_FONT};
  font-size: ${SMALL_SECONDARY_SIZE};
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;
export const ProjectSkill = styled.li`
  margin: 0;
  padding: 0.5rem 0.9rem;
  white-space: nowrap;
  font-family: ${SECONDARY_FONT};
  font-size: ${SMALL_SECONDARY_SIZE};
  color: ${GAINSBORO};
  border: 1px solid rgba(218, 221, 216, 0.2);
  border-radius: 999px;

  a {
    color: ${GHOST_WHITE};
    text-decoration: none;

    &:hover,
    &:focus-visible {
      color: ${GAINSBORO};
    }
  }

  @media ${MediaQuery.isTablet} {
    padding: 0.4rem 0.8rem;
  }

  @media ${MediaQuery.isMobile} {
    padding: 0.35rem 0.55rem;
    font-size: 1.2rem;
  }
`;

export const ProjectStack = styled.ul`
  list-style-type: none;
  margin-top: 2rem;
  display: flex;
  flex-wrap: nowrap;
  gap: 0.8rem;
  justify-content: ${(props) => (props.$reversed ? "flex-start" : "flex-end")};

  @media ${MediaQuery.isTablet} {
    justify-content: flex-start;
  }

  @media ${MediaQuery.isMobile} {
    gap: 0.4rem;
    justify-content: flex-start;
  }
`;

export const Project = styled.div`
  display: flex;
  flex-direction: ${(props) => (props.$reversed ? "row-reverse" : "row")};
  align-items: center;

  @media ${MediaQuery.isTablet} {
    flex-direction: column;
    width: 100%;
  }

  @media ${MediaQuery.isMobile} {
    flex-direction: column;
    width: 100%;
    align-items: stretch;
  }
`;
